// imports
import { gsap } from "gsap"
import * as math from "mathjs"
import * as THREE from 'three';


// classes
import Experience from "../Experience"

export default class CharacterControls
{
    constructor()
    {
        // setup
        this.experience = new Experience()
        this.resource = this.experience.world.fox.resource
        this.character = this.experience.world.fox.instance
        this.debug = this.experience.debug
        this.animationController = this.experience.animationController
        this.camera = this.experience.camera

        // options
        this.keysPressed =
        {
            w: false,
            a: false,
            s: false,
            d: false,
            
            shift: false
        }
        this.directionStates =
        {
            forward: "forward",
            backward: "backward",
            left: "left",
            right: "right"
        }

        this.setCharacterControllerInput()
        setInterval( () => this.finiteStateMachine(), 500 )
    }

    setCharacterControllerInput()
    {
        this.camera.instance.lookAt( this.character.position )
        document.addEventListener( "keydown", ( _event ) => this.handleKeyStatus( true, _event ) )
        document.addEventListener( "keyup", ( _event ) => this.handleKeyStatus( false, _event ) )
    }

    handleKeyStatus( _status, _event )
    {
        if ( _event.shiftKey && _event.key.toLowerCase() === "shift" )
        {
            this.keysPressed[ "shift" ] = _status
        }
        if ( _event.key.toLowerCase() in this.keysPressed )
        {
            this.keysPressed[ _event.key.toLowerCase() ] = _status
        }

        this.finiteStateMachine()
    }

    finiteStateMachine()
    {
        this.animationStates =
        {
            idle: "idle",
            survey: "survey",
            walk: "walk",
            run: "run"
        }

        this.currentAnimationState = null
        this.currentDirectionState = []

        if ( this.keysPressed[ "w" ] )
        {
            this.currentAnimationState = this.animationStates.walk
            this.currentDirectionState.push( this.directionStates.forward )
        }
        if ( this.keysPressed[ "a" ] )
        {
            this.currentAnimationState = this.animationStates.walk
            this.currentDirectionState.push( this.directionStates.left )
        }
        if ( this.keysPressed[ "s" ] )
        {
            this.currentAnimationState = this.animationStates.walk
            this.currentDirectionState.push( this.directionStates.backward )
        }
        if ( this.keysPressed[ "d" ] )
        {
            this.currentAnimationState = this.animationStates.walk
            this.currentDirectionState.push( this.directionStates.right )
        }

        if (
            (
            this.keysPressed[ "w" ] || 
            this.keysPressed[ "a" ] || 
            this.keysPressed[ "s" ] || 
            this.keysPressed[ "d" ] 
            ) && this.keysPressed[ "shift" ]
        )
        {
            this.currentAnimationState = this.animationStates.run
        }

        if (
        !( this.keysPressed[ "w" ] || 
        this.keysPressed[ "a" ] || 
        this.keysPressed[ "s" ] || 
        this.keysPressed[ "d" ] ) )
        {
            this.currentAnimationState = this.animationStates.idle
        }

        if ( this.currentDirectionState !== [] )
            this.moveCharacter()
        
        if ( this.currentAnimationState !== null && this.currentAnimationState !== "survey" )
            this.animationController.animateState( this.character, this.resource, this.currentAnimationState )
    }

    moveCharacter()
    {
        this.directionAxis = 
        {
            forward: "z",
            backward: "z",
            left: "x",
            right: "x"
        }
        this.directionSign =
        {
            forward: "+",
            backward: "-",
            left: "+",
            right: "-"
        }
        this.rotationFormula =
        {
            forward: 0,
            backward: -Math.PI,
            left: Math.PI * 0.5,
            right: -Math.PI * 0.5
        }

        this.movmentParamters =
        {
            animationDuration: 1.0,
            walkSpeed: 1.0,
            runSpeed: 1.3
        }
        this.movmentSpeed = this.currentAnimationState === "walk" ? this.movmentParamters.walkSpeed : this.movmentParamters.runSpeed        

        const gsapRotate = ( _direction ) =>
        {
            if ( _direction === "forward" )
            {
                gsap.to( this.character.rotation, 
                {
                    duration: this.movmentParamters.animationDuration,
                    y: this.rotationFormula.forward
                } )
                this.directionStates.forward = "forward"
                this.directionStates.backward = "backward"
                this.directionStates.left = "left"
                this.directionStates.right = "right"

            }
            if ( _direction === "backward" )
            {
                gsap.to( this.character.rotation, 
                {
                    duration: this.movmentParamters.animationDuration,
                    y: this.rotationFormula.backward
                } )
                this.directionStates.forward = "backward"
                this.directionStates.backward = "forward"
                this.directionStates.left = "right"
                this.directionStates.right = "left"

                this.rotationFormula.left = Math.PI
            }

            if ( _direction === "left" )
            {
                gsap.to( this.character.rotation, 
                {
                    duration: this.movmentParamters.animationDuration,
                    y: this.rotationFormula.left
                } )
                this.directionStates.forward = "left"
                this.directionStates.backward = "right"
                this.directionStates.left = "backward"
                this.directionStates.right = "forward"

                this.rotationFormula.backward =  Math.PI
            }
            if ( _direction === "right" )
            {
                gsap.to( this.character.rotation, 
                {
                    duration: this.movmentParamters.animationDuration,
                    y: this.rotationFormula.right
                } )
                this.directionStates.forward = "right"
                this.directionStates.backward = "left"
                this.directionStates.left = "forward"
                this.directionStates.right = "backward"
            }
        }
        
        const gsapMove = ( _direction, _axis ) =>
        {
            this.axisValue = this.character.position[ _axis ]
            this.directionValue = math.evaluate(
              `${ this.axisValue } ${ this.directionSign[_direction] } ${ this.movmentSpeed }`
            )
          
            return gsap.to( this.character.position,
            {
                duration: this.movmentParamters.animationDuration,
                [_axis]: this.directionValue,
            } );
        }

        this.currentDirectionState.forEach( _direction =>
        {
            gsapRotate( _direction )
            gsapMove( _direction, this.directionAxis[ _direction ] )
            this.moveCamera()
        } )
    }

    moveCamera()
    {
        gsap.to( this.camera.instance.position, 
        {
            duration: this.movmentParamters.animationDuration,
            x: this.character.position.x + this.camera.instanceParameters.position.x,
            y: this.camera.instanceParameters.position.y,
            z: this.camera.instanceParameters.position.z + this.character.position.z
        } )
        // gsap.to( this.camera.instance.rotation, 
        //     {
        //         duration: this.movmentParamters.animationDuration,
        //         x: this.camera.instance.rotation.x, //+ this.camera.instanceParameters.rotation.x,
        //         y: this.camera.instance.rotation.y,
        //         z: this.camera.instance.rotation.z //- this.camera.instanceParameters.rotation.z
        //     } )
        this.camera.instance.lookAt( this.character.position )
    }

    setDebug()
    {
        this.folder = this.experience.controls.tabs.pages[1]
    }
}